import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { JabalipuraanLogo } from '../components/common/JabalipuraanLogo';

// Google icon SVG
const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function Register() {
  const { loginWithGoogle } = useAuth();

  const handleGoogleSignUp = () => {
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="register-page">
      {/* Left Panel - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-accent rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12 text-center">
          <JabalipuraanLogo size="xl" linkTo="/" className="mb-8" />
          <h2 className="font-heading text-3xl font-bold text-primary-foreground mb-4">
            Join Jabalipuraan
          </h2>
          <p className="font-accent text-xl italic text-primary-foreground/80 max-w-md mb-8">
            "Heritage by Jabalpuriyas, for Jabalpuriyas, and of Jabalpuriyas."
          </p>
          <ul className="text-left text-primary-foreground/80 space-y-2">
            <li>✓ Free access to full heritage articles</li>
            <li>✓ View citations & original references</li>
            <li>✓ Support heritage preservation</li>
          </ul>
          <p className="text-sm text-primary-foreground/60 mt-6 italic max-w-xs">
            A public-interest platform — community-supported, open access.
          </p>
        </div>
      </div>

      {/* Right Panel - Google Sign Up */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md text-center"
        >
          <div className="lg:hidden flex justify-center mb-8">
            <JabalipuraanLogo size="lg" linkTo="/" />
          </div>

          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
            Create Free Account
          </h1>
          <p className="text-muted-foreground mb-2">
            Open access to the complete heritage archive.
          </p>
          <p className="text-sm text-muted-foreground/70 mb-8 italic">
            Written for public knowledge and long-term preservation.
          </p>

          {/* Google Sign Up Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-14 font-medium text-base"
            onClick={handleGoogleSignUp}
            data-testid="google-register-btn"
          >
            <GoogleIcon />
            <span className="ml-3">Continue with Google</span>
          </Button>

          <p className="mt-6 text-sm text-muted-foreground">
            Create your account securely using Google.
            <br />
            Works with Gmail and Google Workspace accounts.
          </p>

          <div className="mt-8 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Why Google sign-in?</strong>
              <br />
              For security and convenience, we use Google authentication. 
              Your account is protected by Google's security.
            </p>
          </div>

          <p className="mt-8 text-center text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-accent hover:underline font-medium" data-testid="login-link">
              Sign in here
            </Link>
          </p>

          <p className="mt-4 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
              ← Back to Home
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
